import { createTheme } from '@mui/material';
import {grey} from "@mui/material/colors";

const baseComponents = {
    MuiButton: {
        variants: [
            {
                props: {variant: "contained", color: "grey"},
                style: {
                    backgroundColor: grey[300],
                    color: '#242424',
                },
            },
        ]
    },
    MuiLink: {
        defaultProps: {
            underline: 'none',
        },
    },
};

const darkBaseComponents = {
    MuiButton: {
        styleOverrides: {
            root: { color: '#f6f6f6' },
        },
        variants: [
            {
                props: {variant: "contained", color: "grey"},
                style: {
                    backgroundColor: grey[300],
                    color: '#242424',
                },
            },
        ]
    },
    MuiPaper: {
        styleOverrides: {
            root: { backgroundImage: 'unset' }
        },
    },
    MuiLink: {
        defaultProps: { underline: 'none' },
    },
    MuiDivider: {
        styleOverrides: {
            light: { borderColor: 'rgb(36,42,53)' }
        }
    },
};

function buildTheme(baseTheme, colors) {
    const isDark = baseTheme.palette.mode === 'dark';
    const palette = isDark ? colors.dark : colors.light;

    return createTheme({
        palette: {
            ...baseTheme.palette,
            primary: { main: palette.primary },
            secondary: {
                main: palette.secondary,
                stopWorkoutColor: isDark ? (palette.stopWorkoutColor || '#580505') : (palette.stopWorkoutColor || '#ffcdd2'),
                startWorkoutColor: isDark ? (palette.startWorkoutColor || '#0f3b04') : (palette.startWorkoutColor || '#c8e6c9'),
            },
            ...(palette.background ? { background: palette.background } : {}),
            ...(palette.text ? { text: palette.text } : {}),
            ...(palette.divider ? { divider: palette.divider } : {}),
            contrastThreshold: 2,
            tonalOffset: 0.2,
        },
        components: isDark ? darkBaseComponents : baseComponents,
    });
}

const themeRegistry = {
    theme_amoled_black: {
        id: 'theme_amoled_black',
        name: 'AMOLED Black',
        description: 'Pure black with electric blue accents',
        category: 'dark',
        preview: { primary: '#000000', secondary: '#2196f3', bg: '#000000' },
        light: { primary: '#1565c0', secondary: '#2196f3' },
        dark: {
            primary: '#1565c0',
            secondary: '#2196f3',
            background: { default: '#000000', paper: '#0a0a0a' },
            text: { primary: '#ffffff', secondary: '#b0b0b0' },
            divider: '#1a1a1a',
        },
    },
    theme_midnight_blue: {
        id: 'theme_midnight_blue',
        name: 'Midnight Blue',
        description: 'Deep navy with steel blue accents',
        category: 'dark',
        preview: { primary: '#0a1628', secondary: '#5b8db8', bg: '#0a1628' },
        light: { primary: '#1a3a5c', secondary: '#5b8db8' },
        dark: {
            primary: '#1a3a5c',
            secondary: '#5b8db8',
            background: { default: '#0a1628', paper: '#122240' },
            text: { primary: '#e0e8f0', secondary: '#8faabe' },
            divider: '#1a3050',
        },
    },
    theme_dark_forest: {
        id: 'theme_dark_forest',
        name: 'Dark Forest',
        description: 'Deep green with sage accents',
        category: 'dark',
        preview: { primary: '#0b1a0f', secondary: '#6b8f71', bg: '#0b1a0f' },
        light: { primary: '#2d5a3d', secondary: '#6b8f71' },
        dark: {
            primary: '#3d6b4d',
            secondary: '#6b8f71',
            background: { default: '#0b1a0f', paper: '#142819' },
            text: { primary: '#e0ebe2', secondary: '#8aab8e' },
            divider: '#1e3824',
        },
    },
    theme_charcoal_rose: {
        id: 'theme_charcoal_rose',
        name: 'Charcoal Rose',
        description: 'Dark charcoal with dusty rose accents',
        category: 'dark',
        preview: { primary: '#2a2025', secondary: '#c48b9f', bg: '#1a1518' },
        light: { primary: '#6b4c5a', secondary: '#c48b9f' },
        dark: {
            primary: '#6b4c5a',
            secondary: '#c48b9f',
            background: { default: '#1a1518', paper: '#2a2025' },
            text: { primary: '#f0e4e8', secondary: '#b8a0aa' },
            divider: '#3a2830',
        },
    },
    theme_obsidian: {
        id: 'theme_obsidian',
        name: 'Obsidian',
        description: 'Near-black with amber gold accents',
        category: 'dark',
        preview: { primary: '#1a1614', secondary: '#d4a04a', bg: '#1a1614' },
        light: { primary: '#5a4830', secondary: '#d4a04a' },
        dark: {
            primary: '#5a4830',
            secondary: '#d4a04a',
            background: { default: '#1a1614', paper: '#252018' },
            text: { primary: '#f0e8d8', secondary: '#b8a888' },
            divider: '#302818',
        },
    },
    theme_ocean: {
        id: 'theme_ocean',
        name: 'Ocean',
        description: 'Deep teal with coral accents',
        category: 'vibrant',
        preview: { primary: '#1a535e', secondary: '#ff6b6b', bg: '#e8f4f6' },
        light: { primary: '#1a535e', secondary: '#ff6b6b' },
        dark: {
            primary: '#1a535e',
            secondary: '#ff6b6b',
            background: { default: '#0e2e33', paper: '#163a40' },
            text: { primary: '#e0f0f2', secondary: '#88b8be' },
            divider: '#1e4a52',
        },
    },
    theme_sunset: {
        id: 'theme_sunset',
        name: 'Sunset',
        description: 'Burnt orange with deep purple accents',
        category: 'vibrant',
        preview: { primary: '#e8651a', secondary: '#7b2d8e', bg: '#fff5ee' },
        light: { primary: '#c85a10', secondary: '#7b2d8e' },
        dark: {
            primary: '#e8651a',
            secondary: '#9b4dca',
            background: { default: '#1a1018', paper: '#261822' },
            text: { primary: '#f5e8e0', secondary: '#c8a898' },
            divider: '#3a2030',
        },
    },
    theme_arctic: {
        id: 'theme_arctic',
        name: 'Arctic',
        description: 'Icy blues with crisp navy accents',
        category: 'vibrant',
        preview: { primary: '#1b3a4b', secondary: '#5fa8d3', bg: '#e8f1f5' },
        light: {
            primary: '#1b3a4b',
            secondary: '#5fa8d3',
            background: { default: '#e8f1f5', paper: '#f0f7fa' },
        },
        dark: {
            primary: '#1b3a4b',
            secondary: '#5fa8d3',
            background: { default: '#0a1820', paper: '#122830' },
            text: { primary: '#e0eef5', secondary: '#88b0c8' },
            divider: '#1a3040',
        },
    },
    theme_neon_gym: {
        id: 'theme_neon_gym',
        name: 'Neon Gym',
        description: 'Dark with electric neon green',
        category: 'vibrant',
        preview: { primary: '#121212', secondary: '#39ff14', bg: '#121212' },
        light: { primary: '#1a3a1a', secondary: '#2ecc40' },
        dark: {
            primary: '#2a4a2a',
            secondary: '#39ff14',
            background: { default: '#0a0f0a', paper: '#121a12' },
            text: { primary: '#e0f5e0', secondary: '#80c080' },
            divider: '#1a2a1a',
        },
    },
    theme_lava: {
        id: 'theme_lava',
        name: 'Lava',
        description: 'Dark with deep red and ember accents',
        category: 'vibrant',
        preview: { primary: '#8b0000', secondary: '#ff4500', bg: '#1a0a0a' },
        light: { primary: '#8b0000', secondary: '#ff4500' },
        dark: {
            primary: '#8b0000',
            secondary: '#ff4500',
            background: { default: '#1a0a0a', paper: '#251010' },
            text: { primary: '#f5e0e0', secondary: '#c88080' },
            divider: '#3a1515',
        },
    },
    theme_minimalist: {
        id: 'theme_minimalist',
        name: 'Minimalist',
        description: 'Clean white with pure black accents',
        category: 'aesthetic',
        preview: { primary: '#1a1a1a', secondary: '#555555', bg: '#fafafa' },
        light: {
            primary: '#1a1a1a',
            secondary: '#555555',
            background: { default: '#fafafa', paper: '#ffffff' },
        },
        dark: {
            primary: '#424242',
            secondary: '#e0e0e0',
            background: { default: '#111111', paper: '#1a1a1a' },
            text: { primary: '#f0f0f0', secondary: '#999999' },
            divider: '#222222',
        },
    },
    theme_warm_paper: {
        id: 'theme_warm_paper',
        name: 'Warm Paper',
        description: 'Cream parchment with terracotta accents',
        category: 'aesthetic',
        preview: { primary: '#3e2c1c', secondary: '#c17a56', bg: '#f5f0e8' },
        light: {
            primary: '#3e2c1c',
            secondary: '#c17a56',
            background: { default: '#f5f0e8', paper: '#faf6f0' },
        },
        dark: {
            primary: '#8b6b4a',
            secondary: '#c17a56',
            background: { default: '#1a1510', paper: '#252018' },
            text: { primary: '#f0e8d8', secondary: '#b8a888' },
            divider: '#302818',
        },
    },
    theme_lavender_haze: {
        id: 'theme_lavender_haze',
        name: 'Lavender Haze',
        description: 'Soft lavender with deep purple accents',
        category: 'aesthetic',
        preview: { primary: '#4a3466', secondary: '#9b7ed8', bg: '#e8e0f0' },
        light: {
            primary: '#4a3466',
            secondary: '#9b7ed8',
            background: { default: '#f0eaf8', paper: '#f6f2fc' },
        },
        dark: {
            primary: '#6b4e8a',
            secondary: '#9b7ed8',
            background: { default: '#14101e', paper: '#1e1830' },
            text: { primary: '#e8e0f5', secondary: '#a898c8' },
            divider: '#2a2040',
        },
    },
    theme_matcha: {
        id: 'theme_matcha',
        name: 'Matcha',
        description: 'Soft sage green with forest accents',
        category: 'aesthetic',
        preview: { primary: '#2d3b2d', secondary: '#7fb069', bg: '#e4ece4' },
        light: {
            primary: '#2d3b2d',
            secondary: '#7fb069',
            background: { default: '#eaf0ea', paper: '#f2f6f2' },
        },
        dark: {
            primary: '#3d6b4a',
            secondary: '#7fb069',
            background: { default: '#101810', paper: '#182218' },
            text: { primary: '#e0ebe0', secondary: '#88ab88' },
            divider: '#1e2e1e',
        },
    },
    theme_concrete: {
        id: 'theme_concrete',
        name: 'Concrete',
        description: 'Industrial gray with caution yellow accents',
        category: 'aesthetic',
        preview: { primary: '#3a3a3a', secondary: '#f2c744', bg: '#d0cfc9' },
        light: {
            primary: '#3a3a3a',
            secondary: '#d4a820',
            background: { default: '#dddcd6', paper: '#e8e7e2' },
        },
        dark: {
            primary: '#808080',
            secondary: '#f2c744',
            background: { default: '#1a1a18', paper: '#252522' },
            text: { primary: '#e8e8e0', secondary: '#a0a098' },
            divider: '#303028',
        },
    },
    theme_nord: {
        id: 'theme_nord',
        name: 'Nord',
        description: 'Arctic blues inspired by polar night',
        category: 'dark',
        preview: { primary: '#2e3440', secondary: '#88c0d0', bg: '#2e3440' },
        light: { primary: '#2e3440', secondary: '#5e81ac' },
        dark: {
            primary: '#3b4252',
            secondary: '#88c0d0',
            background: { default: '#2e3440', paper: '#3b4252' },
            text: { primary: '#eceff4', secondary: '#d8dee9' },
            divider: '#434c5e',
        },
    },
    theme_rose_gold: {
        id: 'theme_rose_gold',
        name: 'Rose Gold',
        description: 'Elegant metallic pink and gold',
        category: 'aesthetic',
        preview: { primary: '#8e4a5e', secondary: '#d4a07a', bg: '#fdf0ec' },
        light: {
            primary: '#8e4a5e',
            secondary: '#d4a07a',
            background: { default: '#fdf0ec', paper: '#fff6f3' },
        },
        dark: {
            primary: '#a0586c',
            secondary: '#d4a07a',
            background: { default: '#1a1214', paper: '#261a1e' },
            text: { primary: '#f5e8eb', secondary: '#c8a8b0' },
            divider: '#3a2028',
        },
    },
    theme_dracula: {
        id: 'theme_dracula',
        name: 'Dracula',
        description: 'Classic dark with vivid pastel accents',
        category: 'dark',
        preview: { primary: '#282a36', secondary: '#bd93f9', bg: '#282a36' },
        light: { primary: '#44475a', secondary: '#8b6fc0' },
        dark: {
            primary: '#44475a',
            secondary: '#bd93f9',
            background: { default: '#282a36', paper: '#343746' },
            text: { primary: '#f8f8f2', secondary: '#6272a4' },
            divider: '#44475a',
        },
    },
    theme_sakura: {
        id: 'theme_sakura',
        name: 'Sakura',
        description: 'Soft cherry blossom pinks',
        category: 'aesthetic',
        preview: { primary: '#5c2a3e', secondary: '#e8879b', bg: '#fef0f3' },
        light: {
            primary: '#5c2a3e',
            secondary: '#e8879b',
            background: { default: '#fef0f3', paper: '#fff5f7' },
        },
        dark: {
            primary: '#8b4060',
            secondary: '#e8879b',
            background: { default: '#1a0e12', paper: '#26141a' },
            text: { primary: '#f8e8ed', secondary: '#c89aa8' },
            divider: '#3a1825',
        },
    },
    theme_copper: {
        id: 'theme_copper',
        name: 'Copper',
        description: 'Rich bronze and warm metallic tones',
        category: 'vibrant',
        preview: { primary: '#5a3520', secondary: '#cd7f32', bg: '#f5ebe0' },
        light: {
            primary: '#5a3520',
            secondary: '#cd7f32',
            background: { default: '#f5ebe0', paper: '#faf3ec' },
        },
        dark: {
            primary: '#7a5035',
            secondary: '#cd7f32',
            background: { default: '#16100a', paper: '#201810' },
            text: { primary: '#f0e4d8', secondary: '#b89878' },
            divider: '#302010',
        },
    },
};

export function applyCustomTheme(baseTheme, themeId) {
    if (!themeId || !themeRegistry[themeId]) return baseTheme;
    return buildTheme(baseTheme, themeRegistry[themeId]);
}

export function getThemePreview(themeId) {
    return themeRegistry[themeId]?.preview || null;
}

export function getAllThemes() {
    return Object.values(themeRegistry);
}

export default themeRegistry;
